import * as C from "../../config/constants.js";

// Container proportions (fraction of bounds width/height)
// Neck = floor: straight-walled Erlenmeyer profile; angled segments connect neck to floor.
// Floor/neck half-width is derived from bounds minus wall thickness so bodies can reach
// the side walls (no 88%-width inset gap at the floor).
var BODY_TOP_FRACTION = 0.28;   // kept for the angled-wall segments (now vertical)

export function getFlaskPanelHeight(bounds) {
  if (!bounds) return 0;
  var top = bounds.top != null ? Number(bounds.top) : 0;
  var bottom = bounds.bottom != null ? Number(bounds.bottom) : top;
  return Math.max(1, bottom - top);
}

export function getFlaskPanelWidth(bounds) {
  if (!bounds) return 0;
  var left = bounds.left != null ? Number(bounds.left) : 0;
  var right = bounds.right != null ? Number(bounds.right) : left;
  return Math.max(1, right - left);
}

/** Scale factor vs reference jar height (Fall mini flask < 1, full Shake jar ≈ 1). */
export function getFlaskPanelScale(bounds, refHeightPx) {
  var ref = refHeightPx != null ? Number(refHeightPx) : (
    C.FLASK_PHYSICS_REF_HEIGHT_PX != null ? Number(C.FLASK_PHYSICS_REF_HEIGHT_PX) : 400
  );
  if (!isFinite(ref) || ref <= 0) ref = 400;
  return getFlaskPanelHeight(bounds) / ref;
}

/** Matter gravity y for this jar (scales with panel height). */
export function getFlaskGravityForBounds(bounds, runConfig) {
  return C.getFlaskGravityForPanelHeight(getFlaskPanelHeight(bounds), runConfig);
}

/** Convert a ref-height pixel/speed value to this jar. */
export function scaleFlaskPhysicsPx(refPx, bounds) {
  var n = Number(refPx);
  if (!isFinite(n)) n = 0;
  var scale = getFlaskPanelScale(bounds);
  if (!isFinite(scale) || scale <= 0) scale = 1;
  return n * scale;
}

export function getFlaskMouthDampSlackPx(bounds) {
  var frac = (C.FLASK_MOUTH_DAMP_SLACK_FRAC != null)
    ? Number(C.FLASK_MOUTH_DAMP_SLACK_FRAC) : 0.005;
  if (!isFinite(frac) || frac < 0) frac = 0.005;
  return Math.max(1, getFlaskPanelHeight(bounds) * frac);
}

export function getFlaskMouthOvershootMinPx(bounds) {
  var frac = (C.FLASK_MOUTH_OVERSHOOT_MIN_FRAC != null)
    ? Number(C.FLASK_MOUTH_OVERSHOOT_MIN_FRAC) : 0.02;
  if (!isFinite(frac) || frac < 0) frac = 0.02;
  return Math.max(1, getFlaskPanelHeight(bounds) * frac);
}

export function getFlaskMouthMaxUpwardSpeed(bounds) {
  var frac = (C.FLASK_MOUTH_MAX_UPWARD_SPEED_FRAC != null)
    ? Number(C.FLASK_MOUTH_MAX_UPWARD_SPEED_FRAC) : 0.015;
  if (!isFinite(frac) || frac <= 0) frac = 0.015;
  return Math.max(2, getFlaskPanelHeight(bounds) * frac);
}

export function getFlaskFloorHalfMinPx(bounds) {
  var frac = (C.FLASK_FLOOR_HALF_MIN_FRAC != null)
    ? Number(C.FLASK_FLOOR_HALF_MIN_FRAC) : 0.02;
  if (!isFinite(frac) || frac < 0) frac = 0.02;
  return Math.max(1, Math.min(getFlaskPanelWidth(bounds), getFlaskPanelHeight(bounds)) * frac);
}

export function getFlaskDropSpawnOffsetFrac() {
  var frac = (C.FLASK_DROP_SPAWN_OFFSET_FRAC != null)
    ? Number(C.FLASK_DROP_SPAWN_OFFSET_FRAC)
    : 0.02;
  if (!isFinite(frac) || frac < 0) frac = 0.02;
  return frac;
}

/** Panel-space pixels below `bounds.top` where merge drops spawn. */
export function getDropSpawnYOffsetPx(bounds) {
  if (!bounds) return 0;
  var top = bounds.top != null ? Number(bounds.top) : 0;
  var bottom = bounds.bottom != null ? Number(bounds.bottom) : top;
  var h = Math.max(1, bottom - top);
  return h * getFlaskDropSpawnOffsetFrac();
}

/** Panel-space Y for merge drop spawn (Matter body center at release). */
export function getDropSpawnY(bounds) {
  if (!bounds) return 0;
  var top = bounds.top != null ? Number(bounds.top) : 0;
  return top + getDropSpawnYOffsetPx(bounds);
}

/**
 * Half-width of the Erlenmeyer interior at a given y.
 * Linear interpolation between neck and floor.
 */
/** Physics one-way mouth gate top — raised above visual rim (`geom.T`). */
export function getPhysicsMouthGateTopY(geom) {
  if (!geom) return 0;
  var T = geom.T;
  var innerH = Math.max(1, geom.bodyBottom - T);
  var raiseFrac = (C.FLASK_MOUTH_GATE_RAISE_FRAC != null)
    ? Number(C.FLASK_MOUTH_GATE_RAISE_FRAC)
    : 0.05;
  if (!isFinite(raiseFrac) || raiseFrac < 0) raiseFrac = 0.05;
  var extendFrac = geom.physicsWallExtendUpFrac != null
    ? Number(geom.physicsWallExtendUpFrac)
    : 0;
  if (!isFinite(extendFrac) || extendFrac < 0) extendFrac = 0;
  return T - innerH * (raiseFrac + extendFrac);
}

export function getErlenmeyerHalfWidthAtY(geom, y) {
  var neckHalfW = (geom.neckRight - geom.neckLeft) / 2;
  var floorHalfW = geom.floorHalfWidth;
  if (y <= geom.bodyTop) return neckHalfW;
  if (y >= geom.bodyBottom) return floorHalfW;
  var t = (y - geom.bodyTop) / (geom.bodyBottom - geom.bodyTop);
  return neckHalfW + t * (floorHalfW - neckHalfW);
}

/** Legacy ellipse helper kept for backward-compat with any remaining callers. */
export function getEllipseHalfWidthAtY(cx, cy, rx, ry, y) {
  if (!(rx > 0) || !(ry > 0)) return 0;
  var normY = (y - cy) / ry;
  return rx * Math.sqrt(Math.max(0, 1 - normY * normY));
}

export function buildFlaskGeometry(bounds, wallThickness, buildOpts) {
  var L = bounds.left;
  var R = bounds.right;
  var T = bounds.top;
  var B = bounds.bottom;
  var W = wallThickness;
  var extendFrac = buildOpts && buildOpts.physicsWallExtendUpFrac != null
    ? Number(buildOpts.physicsWallExtendUpFrac)
    : 0;
  if (!isFinite(extendFrac) || extendFrac < 0) extendFrac = 0;
  var w = R - L;
  var h = B - T;
  var cx = (L + R) / 2;

  var bodyTop = T + h * BODY_TOP_FRACTION;
  var bodyBottom = B;
  // Floor segment width is 2*floorHalfW + 2*W centered on cx → floorHalfW = w/2 − W touches bounds inner wall.
  var floorHalfW = Math.max(getFlaskFloorHalfMinPx(bounds), w / 2 - W);
  var neckHalfW = floorHalfW;

  var neckLeft = cx - neckHalfW;
  var neckRight = cx + neckHalfW;
  var floorLeft = cx - floorHalfW;
  var floorRight = cx + floorHalfW;

  // Horizontal playfield limits (body center): inner face of side walls is neckLeft/neckRight
  // (= L+W / R−W), same as the stroked flask outline — not floorLeft±W (double inset).
  var interior = {
    minX: neckLeft,
    maxX: neckRight,
    neckMinX: neckLeft + W / 2,
    neckMaxX: neckRight - W / 2,
    minY: bodyTop + W / 2,
    maxY: bodyBottom - W,
    bodyTop: bodyTop,
  };

  var geometry = {
    flaskType: "erlenmeyer",
    cx: cx,
    cy: (bodyTop + bodyBottom) / 2,
    // Erlenmeyer-specific
    bodyTop: bodyTop,
    bodyBottom: bodyBottom,
    neckLeft: neckLeft,
    neckRight: neckRight,
    floorLeft: floorLeft,
    floorRight: floorRight,
    floorHalfWidth: floorHalfW,
    // Kept for backward-compat (rough approximations so old call sites don't break)
    sphereR: floorHalfW,
    ellipseRx: floorHalfW,
    ellipseRy: (bodyBottom - bodyTop) / 2,
    cutoffFraction: 0.9,
    cutoffTopFraction: 0.9,
    cutoffBottomFraction: 0.9,
    T: T,
    L: L,
    R: R,
    B: B,
    W: W,
    physicsWallExtendUpFrac: extendFrac,
  };

  return {
    interior: interior,
    geometry: geometry,
    dropSpawnY: getDropSpawnY(bounds),  // just inside the container top — scales with jar height
  };
}
